import {
  EClickableSize,
  EClickableTarget,
} from '../at-clickable/at-clickable.types';

/**
 * Visual style variants for the `AtNavLink` component.
 *
 * - `'inline'` – Typically used for simple in-line text links (e.g., headers, footers)
 * - `'menu'` – Used for dropdown menu links or navigation clusters
 * - `'cta'` – Styled for primary/secondary calls to action (e.g., buttons in nav)
 */
export enum ENavLinkVariants {
  INLINE = 'inline',
  MENU = 'menu',
  CTA = 'cta',
}
/**
 * Props for the `AtNavLink` component.
 */
export interface IAtNavLinkProps {
  /**
   * Optional child elements (e.g., icon, badge) rendered alongside the label.
   */
  children?: React.ReactNode;
  /**
   * Controls the visual size of the nav link (padding, text size).
   */
  size: EClickableSize;
  /**
   * Anchor tag target attribute.
   * Defaults to `_self`. Useful for opening external links in new tabs.
   */
  target?: EClickableTarget;
  /**
   * Destination URL or internal action (e.g., ':openChat', ':openLoginModal').
   * When omitted or using an action, the component renders as a `<button>`.
   */
  href?: string;
  /**
   * Optional callback fired on click.
   * Used for fallback behavior when internal logic doesn't intercept.
   */
  onClick?: () => void;
  /**
   * Optional callback fired on mouse enter (used for hover behavior).
   */
  onMouseEnter?: () => void;

  /**
   * Optional callback fired on mouse leave.
   */
  onMouseLeave?: () => void;
  /**
   * Visual styling variant for the link.
   * Affects color, spacing, and interaction behavior.
   */
  variant: ENavLinkVariants;
  /**
   * Custom `data-testid` for test querying.
   */
  dataTestId?: string;
  /**
   * Tailwind utility classes for additional styling.
   */
  className?: string;
  /**
   * Screen-reader friendly label.
   * Overrides visible label for accessibility purposes.
   */
  ariaLabel?: string;
  /**
   * The main label for the link.
   * Also used for GTM tracking unless overridden by `ariaLabel`.
   */
  label?: string;
  /**
   * Optional React `key` prop for mapping use cases.
   */
  key?: string;
  /**
   * Marks the link as active or currently selected.
   * Applies a `data-selected` attribute and may affect styles.
   */
  selected?: boolean;
  /**
   * Used in GTM tracking to label the interaction source (e.g., 'header', 'footer').
   */
  interactLocation?: string;
  /**
   * If true (default), sends a GTM click or navigation event on interaction.
   */
  shouldSendGTMEvent?: boolean;
}
